// LearnCore Frontend - 任务卡片组件

import React from 'react';
import type { UserTask, TaskStatus, TaskPriority } from '../types/tasks-rewards';

interface TaskCardProps {
  task: UserTask;
  onComplete?: (taskId: string) => void;
  onUpdate?: (taskId: string, updates: Partial<UserTask>) => void;
  onDelete?: (taskId: string) => void;
  showActions?: boolean;
  compact?: boolean;
}

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onComplete,
  onUpdate,
  onDelete,
  showActions = true,
  compact = false
}) => {
  const getStatusColor = (status: TaskStatus): string => {
    switch (status) {
      case 'pending': return 'bg-gray-100 text-gray-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: TaskPriority): string => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status: TaskStatus): string => {
    switch (status) {
      case 'pending': return '待开始';
      case 'in_progress': return '进行中';
      case 'completed': return '已完成';
      case 'expired': return '已过期';
      default: return status;
    }
  };

  const getCategoryText = (category: string): string => {
    switch (category) {
      case 'study_time': return '学习时间';
      case 'cards_completed': return '完成卡片';
      case 'accuracy_rate': return '正确率';
      case 'consecutive_days': return '连续天数';
      case 'total_mastered': return '掌握词汇';
      case 'content_created': return '创建内容';
      case 'custom': return '自定义';
      default: return category;
    }
  };

  const getTypeText = (type: string): string => {
    switch (type) {
      case 'daily': return '日常';
      case 'habit': return '习惯';
      case 'achievement': return '成就';
      case 'custom': return '自定义';
      default: return type;
    }
  };

  const getPriorityText = (priority: TaskPriority): string => {
    switch (priority) {
      case 'high': return '高';
      case 'medium': return '中';
      case 'low': return '低';
      default: return priority;
    }
  };

  const progressPercentage = task.target_value > 0 
    ? Math.round((task.current_value / task.target_value) * 100)
    : 0;

  const isOverdue = task.due_date && task.due_date < Date.now() && task.status !== 'completed';

  const handleComplete = () => {
    if (onComplete && task.status !== 'completed') {
      onComplete(task.id);
    }
  };

  const handleStatusChange = (newStatus: TaskStatus) => {
    if (onUpdate) {
      onUpdate(task.id, { status: newStatus });
    }
  };

  const handleDelete = () => {
    if (onDelete && window.confirm('确定要删除这个任务吗？')) {
      onDelete(task.id);
    }
  };

  if (compact) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border ${isOverdue ? 'border-red-300' : 'border-gray-200 dark:border-gray-700'} p-3 hover:shadow-md transition-shadow`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${getPriorityColor(task.priority)}`} />
              <h4 className="font-medium text-gray-900 dark:text-gray-100 truncate">{task.title}</h4>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(task.status)}`}>
                {getStatusText(task.status)}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {task.current_value}/{task.target_value}
              </span>
              {task.dkp_reward > 0 && (
                <span className="text-xs font-medium text-orange-600">
                  +{task.dkp_reward} 金币
                </span>
              )}
            </div>
          </div>
          {showActions && task.status !== 'completed' && (
            <button
              onClick={handleComplete}
              className="ml-2 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              完成
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border ${isOverdue ? 'border-red-300' : 'border-gray-200 dark:border-gray-700'} p-4 hover:shadow-md transition-shadow`}>
      {/* 任务头部 */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <div className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`} />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{task.title}</h3>
            {isOverdue && (
              <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">已过期</span>
            )}
          </div>
          {task.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{task.description}</p>
          )}
        </div>
        {showActions && (
          <div className="flex items-center gap-1">
            {task.status !== 'completed' && (
              <button
                onClick={handleComplete}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                完成
              </button>
            )}
            <button
              onClick={handleDelete}
              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* 任务信息 */}
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(task.status)}`}>
          {getStatusText(task.status)}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {getCategoryText(task.category)}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {getTypeText(task.type)}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {getPriorityText(task.priority)}优先级
        </span>
      </div>

      {/* 进度条 */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-gray-600 dark:text-gray-400">进度</span>
          <span className="font-medium">{task.current_value}/{task.target_value}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(progressPercentage, 100)}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>{progressPercentage}% 完成</span>
          {task.dkp_reward > 0 && (
            <span className="font-medium text-orange-600">+{task.dkp_reward} 金币</span>
          )}
        </div>
      </div>

      {/* 截止日期 */}
      {task.due_date && (
        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          截止日期: {new Date(task.due_date).toLocaleDateString('zh-CN')}
        </div>
      )}

      {/* 状态切换（仅在非完成状态显示） */}
      {showActions && task.status !== 'completed' && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">状态:</span>
            <select
              value={task.status}
              onChange={(e) => handleStatusChange(e.target.value as TaskStatus)}
              className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="pending">待开始</option>
              <option value="in_progress">进行中</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskCard;